import type { DefaultPaginatedFilters, PaginatedResponse } from "@/api/shared/filters";
import type { InfiniteData, UseInfiniteQueryReturnType } from "@tanstack/vue-query";
import { skipToken, useInfiniteQuery } from "@tanstack/vue-query";
import { computed, toRef, toValue, type Ref, type WritableComputedRef } from "vue";

import { useQueryEnabled } from "./useQueryEnabled";
import { logError } from "@/utils/AppError";
import { djangoViewsetErrorHandler } from "@/utils/DjangoError";
import { useAbility } from "@casl/vue";
import {
  DEFAULT_LIST_NO_RETRY_STATUS_CODES,
  DEFAULT_STALE_TIME,
  mergeNoRetryStatusCodes,
  shouldSuppressQueryError,
  type TSuppressErrors,
  type TQueryBehaviorOptions,
} from "./baseQueryOptions";
import { useGenericQueryKey } from "./useGenericQueryKey";
import { useOwnedFilterRefs, type TOwnedFilterParams } from "./useOwnedFilterRefs";
import type { TFilterFieldMap, TFilterFields } from "./useFilterFields";
// Re-export for callers that previously imported filter params via the paginated options shape.
export type { TOwnedFilterParams } from "./useOwnedFilterRefs";
import type { NestedKeyOf, OneOrManyOrNull } from "@/types/general";
import type { ESubjects, TPermissionAction } from "@/api/models/auth/ability";
import type { MaybeRefOrGetter } from "vue";
import type { TSortItem } from "@/composables/shared/filters/useUrlSort";

/**
 * Parameters passed to the fetcher function of a paginated query.
 */
export type TGenericPaginatedQueryFetchParams<TFilters> = {
  /**
   * The current filters applied to the query.
   */
  filters: TFilters;
  /**
   * The pagination parameter (e.g., a cursor, cursor ID, or page URL string).
   */
  pageParam?: string;
};

/**
 * Configuration options for the `useGenericPaginatedQuery` composable.
 *
 * Filter ownership lives here: pass `params.initial` / `params.url` to own filters typed
 * from `TFilters`, or inject `params.filters` when a parent already owns them.
 *
 * @template TResponseItem - The type of a single item in the paginated response array.
 * @template TFilters - The type of the filters object passed to the fetcher.
 */
export type TGenericPaginatedQueryOptions<
  TResponseItem,
  TFilters extends Partial<DefaultPaginatedFilters>,
  TFields extends TFilterFieldMap = TFilterFieldMap,
> = TQueryBehaviorOptions & {
  queryKey: MaybeRefOrGetter<readonly any[]>;
  permission?: MaybeRefOrGetter<OneOrManyOrNull<ESubjects>>;
  action?: MaybeRefOrGetter<TPermissionAction>;
  noRetryStatusCodes?: number[];
  suppressErrors?: TSuppressErrors;
  /**
   * Keep the query enabled while pending terms block the app (every other query is
   * force-disabled). Reserved for the terms documents themselves — the user must be able to
   * read what the acceptance dialog is asking them to accept.
   */
  ignorePendingTermsBlock?: boolean;
  /**
   * Owned / injected filter params. Prefer `initial` (+ optional `url`) over constructing
   * filter refs at the call site — the filter shape is the API `TFilters` interface.
   */
  params?: TOwnedFilterParams<TFilters, TFields>;
  /**
   * An array of property paths within `filters` that must be defined for the query to run.
   *
   * Typed against `Partial<TFilters>` because that is the shape actually held at runtime
   * (`useOwnedFilterRefs` always yields partial filters) — the resolvable path set is
   * identical, since `NestedKeyOf` already unwraps `NonNullable` at every level.
   */
  neededKey?: NestedKeyOf<Partial<TFilters>>[];
  /**
   * The asynchronous function that fetches the paginated list data.
   * Receives both the unref'd `filters` and `pageParam` as an argument.
   *
   * `filters` is `Partial<TFilters>`: owned filters start from `initial` and are mutated
   * field-by-field, so no field is guaranteed present. Assert (`filters.type!`) only for
   * fields a `neededKey` entry already gates.
   *
   * On the numeric paginator the requested page rides along in `filters` as `page` (see
   * `getNumericNextPageParam`), so always forward `filters` to the service unfiltered.
   */
  fetcher: (
    params: TGenericPaginatedQueryFetchParams<Partial<TFilters>>
  ) => Promise<PaginatedResponse<TResponseItem>>;
};

/** TanStack infinite query result augmented with owned filter state. */
export type TGenericPaginatedQueryResult<
  TResponseItem,
  TFilters extends Partial<DefaultPaginatedFilters>,
  TFields extends TFilterFieldMap = TFilterFieldMap,
> = UseInfiniteQueryReturnType<
  InfiniteData<PaginatedResponse<TResponseItem>, string | undefined>,
  Error
> & {
  filters: Ref<Partial<TFilters>>;
  queryEnabled: Ref<boolean>;
  /**
   * Stable string of the current filter values — use as `:reset-key` / `:key` on infinite
   * lists. Prefer this over `JSON.stringify(query.filters)` (nested refs are not
   * auto-unwrapped in templates and stringify Ref internals, remounting on every tick).
   */
  filtersResetKey: Ref<string>;
  /**
   * Present when `params.url` is set — bind to the data table's `v-model:options`.
   * Always declared (not conditionally spread) so the result shape is statically known and
   * needs no cast; it is `undefined` in the non-URL modes.
   */
  options: WritableComputedRef<{ sortBy: TSortItem[] }> | undefined;
  /**
   * Writable models for `params.fields`, bound directly as `v-model="query.f.<name>"`.
   *
   * Each model owns its URL params and its request-filter keys, so the page holds no
   * filter state and the input components need no URL wiring of their own.
   */
  f: TFilterFields<TFields>;
  /** Clears every declared field — models, URL params, and the filter keys they own. */
  resetFields: () => void;
};

/**
 * Next-page param for endpoints on the numeric paginator, which answers
 * `{ total, page, page_size, results }` and carries no `next` URL — without this those lists
 * would stop after their first page. Fields are read defensively because `PaginatedResponse`
 * only declares the cursor pair; a response without all three numbers is not this paginator.
 */
const getNumericNextPageParam = (response: unknown): string | undefined => {
  if (typeof response !== "object" || response === null) return undefined;
  const { total, page, page_size: pageSize } = response as Record<string, unknown>;
  if (typeof total !== "number" || typeof page !== "number" || typeof pageSize !== "number") {
    return undefined;
  }
  if (pageSize <= 0 || page * pageSize >= total) return undefined;
  return String(page + 1);
};

const parseNumericPageParam = (pageParam?: string): number | undefined =>
  pageParam !== undefined && /^\d+$/.test(pageParam) ? Number(pageParam) : undefined;

/**
 * A generic Vue Query composable to handle fetching paginated data using TanStack Query's `useInfiniteQuery`.
 * Owns filter refs from the API filter interface by default (`params.initial` / `params.url`).
 *
 * @template TResponseItem - The type of a single item returned in the list.
 * @template TFilters - The type of the filters object.
 *
 * @param {TGenericPaginatedQueryOptions<TResponseItem, TFilters>} options - Configuration options for the infinite query.
 * @returns The Vue Infinite Query result, augmented with `filters`, `queryEnabled`, `filtersResetKey`, and optional `options` (URL table mode).
 */
export const useGenericPaginatedQuery = <
  TResponseItem,
  TFilters extends Partial<DefaultPaginatedFilters>,
  TFields extends TFilterFieldMap = TFilterFieldMap,
>(
  options: TGenericPaginatedQueryOptions<TResponseItem, TFilters, TFields>
): TGenericPaginatedQueryResult<TResponseItem, TFilters, TFields> => {
  const {
    filters: refFilters,
    queryEnabled,
    options: tableOptions,
    f,
    resetFields,
  } = useOwnedFilterRefs<TFilters, TFields>(options.params ?? {});
  const refQueryKey = toRef(options.queryKey) as Ref<readonly any[]>;
  const queryKey = useGenericQueryKey(refQueryKey, refFilters);
  const { can } = useAbility();

  // Pin the generic to the filter type we actually hold. `useOwnedFilterRefs` returns
  // `Partial<TFilters>`, so that — not `TFilters` — is what `neededKey` paths resolve against;
  // leaving it to inference makes `NestedKeyOf` disagree with `neededKey`'s declared type.
  const isEnabled = useQueryEnabled<Partial<TFilters>>({
    enabled: queryEnabled,
    permission: options.permission,
    action: options.action,
    neededKey: options.neededKey,
    filters: refFilters,
    can: can,
    ignorePendingTermsBlock: options.ignorePendingTermsBlock,
  });

  const noRetryStatusCodes = mergeNoRetryStatusCodes(
    DEFAULT_LIST_NO_RETRY_STATUS_CODES,
    options.noRetryStatusCodes
  );

  const fetchPage = async ({ pageParam }: { pageParam?: string }) => {
    // A cursor `pageParam` is used verbatim as the request URL by the service layer, so a
    // derived page number cannot travel that way — it goes out as a `page` query filter.
    const numericPage = parseNumericPageParam(pageParam);
    const pageFilters =
      numericPage === undefined ? refFilters.value : { ...refFilters.value, page: numericPage };
    try {
      return await options.fetcher({
        filters: pageFilters,
        pageParam: numericPage === undefined ? pageParam : undefined,
      });
    } catch (error) {
      const safeStringify = (val: unknown) => {
        try {
          return JSON.stringify(toValue(val));
        } catch {
          return "[unserializable]";
        }
      };
      logError(error, {
        context: {
          feature: "query",
          step: "fetcher",
          queryKey: safeStringify(refQueryKey),
          filters: safeStringify(refFilters),
          pageParam,
        },
      });
      if (!shouldSuppressQueryError(options.suppressErrors, error)) {
        djangoViewsetErrorHandler()(error);
      }
      throw error;
    }
  };

  const query = useInfiniteQuery<
    PaginatedResponse<TResponseItem>,
    Error,
    InfiniteData<PaginatedResponse<TResponseItem>, string | undefined>,
    any[],
    string | undefined
  >({
    queryKey: queryKey as any,
    // `enabled` only governs TanStack's *automatic* fetching; an explicit `refetch()` /
    // `fetchNextPage()` bypasses it. Swapping in `skipToken` while disabled makes the query a
    // genuine no-op — the fetcher is never invoked — and it resumes when `isEnabled` flips true.
    queryFn: computed(() => (isEnabled.value ? fetchPage : skipToken)) as any,
    staleTime: options.staleTime ?? DEFAULT_STALE_TIME,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
    refetchOnReconnect: options.refetchOnReconnect,
    getPreviousPageParam: (last) => last.previous ?? undefined,
    getNextPageParam: (first) => first.next ?? getNumericNextPageParam(first),
    initialPageParam: undefined,

    enabled: isEnabled,
    retry: (failureCount: number, error: any) => {
      const status = error?.response?.status;
      if (status && noRetryStatusCodes.has(status)) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const filtersResetKey = computed(() => JSON.stringify(toValue(refFilters)));

  return Object.assign(query, {
    filters: refFilters,
    queryEnabled,
    filtersResetKey,
    options: tableOptions,
    f,
    resetFields,
  });
};
